/**
 * rAF flights for the guide characters: transform only, one layout read at the start (the path
 * computation), one motion per element (a new flight cancels the previous one and starts from where
 * it is, which is the 120ms blend: the eased start hides the hand-over). Rotation leads translation
 * by ~60ms on arcs. Honours the slow-motion time scale.
 */
import { easeIn, easeInOut, easeOut, timeScale } from './easing';

export type P = { x: number; y: number };
export type Pose = { x: number; y: number; r: number; sh: number };

export type Flight = {
  from: P;
  to: P;
  ms: number;
  /** quadratic lift above the higher end (px); 0 for a straight hop */
  lift: number;
  /** control-point drift along x (px, signed) */
  drift?: number;
  /** cubic path: an explicit second control point overrides lift/drift */
  c1?: P;
  c2?: P;
  rFrom?: number;
  rTo?: number;
  ease?: 'in-out' | 'out' | 'in';
  onFrame?: (pose: Pose, t: number) => void;
  onDone?: () => void;
};

const LEAD_MS = 60;

export function pointAt(f: Flight, t: number): P {
  const { from: a, to: b } = f;
  if (f.c1 && f.c2) {
    const u = 1 - t;
    const x = u * u * u * a.x + 3 * u * u * t * f.c1.x + 3 * u * t * t * f.c2.x + t * t * t * b.x;
    const y = u * u * u * a.y + 3 * u * u * t * f.c1.y + 3 * u * t * t * f.c2.y + t * t * t * b.y;
    return { x, y };
  }
  const c = { x: (a.x + b.x) / 2 + (f.drift ?? 0), y: Math.min(a.y, b.y) - f.lift };
  const u = 1 - t;
  return { x: u * u * a.x + 2 * u * t * c.x + t * t * b.x, y: u * u * a.y + 2 * u * t * c.y + t * t * b.y };
}

/** Starts a flight; returns a cancel function. The pose is handed to onFrame, never written here. */
export function fly(f: Flight): () => void {
  const ease = f.ease === 'out' ? easeOut : f.ease === 'in' ? easeIn : easeInOut;
  const ms = f.ms * timeScale();
  const t0 = performance.now();
  let id = 0;
  const step = () => {
    const now = performance.now();
    const t = Math.min(1, (now - t0) / ms);
    const e = ease(t);
    const p = pointAt(f, e);
    const tr = Math.min(1, (now - t0 + LEAD_MS * timeScale()) / ms);
    const r = (f.rFrom ?? 0) + ((f.rTo ?? 0) - (f.rFrom ?? 0)) * ease(tr);
    f.onFrame?.({ x: p.x, y: p.y, r, sh: Math.min(1, 0.3 + t) }, t);
    if (t < 1) id = requestAnimationFrame(step);
    else f.onDone?.();
  };
  id = requestAnimationFrame(step);
  return () => cancelAnimationFrame(id);
}

/** Timers that honour the slow-motion scale. */
export function after(ms: number, fn: () => void): number {
  return window.setTimeout(fn, ms * timeScale());
}
